import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, canManageUsers, type Alliance, type AuthUser } from '@/lib/api';

export default function AdminAlliances({ user }: { user: AuthUser }) {
  const listQuery = useQuery({ queryKey: ['alliances'], queryFn: api.listAlliances });

  if (!canManageUsers(user.role)) {
    return <div className="text-slate-400">Only R5 can manage alliances.</div>;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Alliances</h1>
        <p className="text-sm text-slate-400">
          All alliances on server{' '}
          {user.alliance?.server ? `#${user.alliance.server.number}` : '—'}. Alliances that use
          this tool are listed automatically and can't be edited here; add any other alliance
          manually (e.g. as leaver target for transfers).
        </p>
      </header>
      <CreateForm />
      <section className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Tag</th>
              <th className="px-3 py-2">Notes</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {listQuery.isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {listQuery.data?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                  No alliances yet.
                </td>
              </tr>
            )}
            {listQuery.data?.map((a) => <RowEditor key={a.id} row={a} />)}
          </tbody>
        </table>
      </section>
      <Style />
    </div>
  );
}

function CreateForm() {
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () =>
      api.createAlliance({
        name: name.trim(),
        tag: tag.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alliances'] });
      setName('');
      setTag('');
      setNotes('');
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
        Create alliance
      </h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate();
        }}
        className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,3fr)_auto]"
      >
        <input
          className="input"
          placeholder="Alliance name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <input
          className="input"
          placeholder="Tag (e.g. FoA)"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          maxLength={16}
        />
        <input
          className="input"
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <button className="btn-primary" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
      </form>
      {error && <div className="text-sm text-red-400">{error}</div>}
    </section>
  );
}

function RowEditor({ row }: { row: Alliance }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name);
  const [tag, setTag] = useState(row.tag ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: () =>
      api.updateAlliance(row.id, {
        name: name.trim(),
        tag: tag.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alliances'] });
      setEditing(false);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  const toggleActive = useMutation({
    mutationFn: () => api.updateAlliance(row.id, { active: !row.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alliances'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteAlliance(row.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alliances'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  if (!editing) {
    return (
      <tr className={row.active ? '' : 'opacity-60'}>
        <td className="px-3 py-2 font-medium">
          {row.name}
          {row.isTenant && (
            <span className="ml-2 inline-flex rounded bg-indigo-900/40 px-2 py-0.5 text-xs text-indigo-300">
              {row.own ? 'Your alliance' : 'Uses this tool'}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-slate-300">{row.tag ?? '—'}</td>
        <td className="px-3 py-2 text-slate-400">{row.notes ?? '—'}</td>
        <td className="px-3 py-2">
          <span
            className={
              row.active
                ? 'inline-flex rounded bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-300'
                : 'inline-flex rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400'
            }
          >
            {row.active ? 'Active' : 'Inactive'}
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          <div className={row.isTenant ? 'hidden' : 'flex justify-end gap-2'}>
            <button
              onClick={() => setEditing(true)}
              className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
            >
              Edit
            </button>
            <button
              onClick={() => toggleActive.mutate()}
              disabled={toggleActive.isPending}
              className={
                row.active
                  ? 'rounded border border-amber-800 px-2 py-1 text-xs text-amber-300 hover:bg-amber-900/40 disabled:opacity-40'
                  : 'rounded border border-emerald-800 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40 disabled:opacity-40'
              }
            >
              {row.active ? 'Deactivate' : 'Reactivate'}
            </button>
            <button
              onClick={() => {
                if (confirm(`Delete alliance "${row.name}"? This cannot be undone.`)) {
                  remove.mutate();
                }
              }}
              disabled={remove.isPending}
              className="rounded border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40 disabled:opacity-40"
            >
              Delete
            </button>
          </div>
          {error && <div className="mt-1 text-right text-xs text-red-400">{error}</div>}
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-slate-900/40">
      <td className="px-3 py-2">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td className="px-3 py-2">
        <input className="input" value={tag} onChange={(e) => setTag(e.target.value)} maxLength={16} />
      </td>
      <td className="px-3 py-2">
        <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </td>
      <td className="px-3 py-2 text-slate-500">{row.active ? 'Active' : 'Inactive'}</td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary">
            Save
          </button>
          <button
            onClick={() => {
              setEditing(false);
              setName(row.name);
              setTag(row.tag ?? '');
              setNotes(row.notes ?? '');
              setError(null);
            }}
            className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
        {error && <div className="mt-1 text-right text-xs text-red-400">{error}</div>}
      </td>
    </tr>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
        width: 100%;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
